/**
 * Grouped Product Configurator Custom Element
 *
 * Manages the multi-product grouped builder:
 *  1. Select a main (outdoor) unit capacity.
 *  2. Pick indoor units via a modal (supports multiple unit products).
 *  3. Add the entire group to cart via /cart/add.js.
 */
class GroupedConfigurator extends HTMLElement {
  connectedCallback() {
    const configEl = document.getElementById(
      `grouped-config-${this.dataset.sectionId}`,
    );
    if (!configEl) return;

    this.config = JSON.parse(configEl.textContent);
    this.selectedMainCapacity = null;
    this.selectedMainVariant = null;
    this.currentRule = null;
    this.unitSlots = []; // Array of { productId, variantId, capacity }
    this.isAddingToCart = false;

    // Modal state
    this.modalSelectedProduct = null;
    this.modalSelectedVariant = null;

    // Cache DOM references
    this.dom = {
      mainOptions: this.querySelector('[data-role="main-options"]'),
      mainInventory: this.querySelector('[data-role="main-inventory"]'),
      unitsStep: this.querySelector('[data-step="units"]'),
      unitCounter: this.querySelector('[data-role="unit-counter"]'),
      unitSlotsContainer: this.querySelector('[data-role="unit-slots"]'),
      addUnitBtn: this.querySelector('[data-role="add-unit"]'),
      summary: this.querySelector('[data-role="summary"]'),
      summaryItems: this.querySelector('[data-role="summary-items"]'),
      totalPrice: this.querySelector('[data-role="total-price"]'),
      comboStatus: this.querySelector('[data-role="combo-status"]'),
      addToCartBtn: this.querySelector('[data-role="add-to-cart"]'),
      modal: this.querySelector('[data-role="unit-modal"]'),
      modalProducts: this.querySelector('[data-role="modal-products"]'),
      modalDetail: this.querySelector('[data-role="modal-detail"]'),
      modalClose: this.querySelector('[data-role="modal-close"]'),
    };

    this.bindEvents();
    this.logVariantStatus();
  }

  logVariantStatus() {
    console.group("🔍 Grouped Configurator — Variants Availability Diagnosis");

    if (this.config?.mainProduct?.variants) {
      console.log(`📦 Main Product: "${this.config.mainProduct.title}" (ID: ${this.config.mainProduct.id})`);
      this.config.mainProduct.variants.forEach((v) => {
        const hasRule = this.config.rules?.some((r) => r.mainCapacity === v.capacity);
        let reason = "Available";
        if (!v.available) {
          reason = "Shopify v.available is false (Out of Stock / inventory policy denies backorders)";
        } else if (!hasRule) {
          reason = "No matching grouped_product_rules rule found for this capacity";
        }

        console.log(
          `  • Variant "${v.title}" | Capacity: ${v.capacity} BTU | Status: ${v.available && hasRule ? "✅ ACTIVE" : "❌ DISABLED"} | Reason: ${reason}`,
          {
            variantId: v.id,
            capacity: v.capacity,
            shopifyAvailable: v.available,
            hasMatchingRule: hasRule,
            locationStock: v.locationStock,
          },
        );
      });
    }

    if (this.config?.unitProducts) {
      console.log(`\n🧩 Unit Products (${this.config.unitProducts.length}):`);
      this.config.unitProducts.forEach((up) => {
        console.groupCollapsed(`Product: "${up.title}" (ID: ${up.id})`);
        up.variants.forEach((v) => {
          console.log(
            `  • Variant "${v.title}" | Capacity: ${v.capacity} BTU | Available: ${v.available} | Qty: ${v.inventoryQuantity}`,
            {
              variantId: v.id,
              shopifyAvailable: v.available,
              inventoryQuantity: v.inventoryQuantity,
              locationStock: v.locationStock,
            },
          );
        });
        console.groupEnd();
      });
    }

    console.groupEnd();
  }

  /**
   * Translated pickup labels handed over by the Liquid block through data-*.
   * Absent when the shop hides stock from this visitor, in which case
   * locationStock is null anyway and nothing is rendered.
   */
  get stockLabels() {
    return {
      available: this.dataset.labelAvailable || "Available for pickup",
      availableCount: this.dataset.labelAvailableCount || "%{count} in stock for pickup",
      unavailable: this.dataset.labelUnavailable || "Unavailable for pickup",
    };
  }

  /**
   * Same "pattern:label" mapping table as blocks/inventory-remaining.liquid,
   * see the comment there for why warehouse names are rewritten.
   * @param {string} locationName
   * @returns {string}
   */
  displayLocationName(locationName) {
    const table = this.dataset.locationNameOverrides || "";

    for (const pair of table.split(",")) {
      const separator = pair.indexOf(":");
      if (separator === -1) continue;

      const pattern = pair.slice(0, separator).trim();
      const label = pair.slice(separator + 1).trim();
      if (pattern && locationName.includes(pattern)) return label;
    }

    return locationName;
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  escapeHtml(value) {
    const div = document.createElement("div");
    div.textContent = value;
    return div.innerHTML;
  }

  renderLocationStockHtml(locationStock) {
    if (!locationStock || typeof locationStock !== "object") return "";
    const keys = Object.keys(locationStock);
    if (keys.length === 0) return "";

    const labels = this.stockLabels;

    let html = '<ul class="inventory-remaining__list list-unstyled" style="margin-top: 0.75rem;">';
    for (const locName of keys) {
      const info = locationStock[locName];
      if (!info) continue;

      const avail = Number(info.available) || 0;
      const isPickup = Boolean(info.isPickupAvailable);
      const safeLocName = this.escapeHtml(this.displayLocationName(locName));

      html += `<li class="inventory-remaining__item">`;
      html += `<span class="inventory-remaining__name">${safeLocName}:&nbsp;</span>`;
      if (isPickup && avail > 0) {
        const text = labels.availableCount.replace("%{count}", String(avail));
        html += `<span class="inventory-remaining__status inventory-remaining__status--available">${this.escapeHtml(
          text
        )}</span>`;
      } else {
        html += `<span class="inventory-remaining__status inventory-remaining__status--unavailable">${this.escapeHtml(
          labels.unavailable
        )}</span>`;
      }
      html += `</li>`;
    }
    html += "</ul>";
    return html;
  }

  /* ------------------------------------------------
   * Event Binding
   * ---------------------------------------------- */
  bindEvents() {
    if (!this.dom) return;

    // Main capacity buttons
    this.dom.mainOptions?.addEventListener("click", (e) => {
      const btn = e.target.closest(".grouped-capacity-btn");
      if (!btn || btn.disabled) return;
      this.selectMain(parseInt(btn.dataset.capacity, 10));
    });

    // Add unit button -> open modal
    this.dom.addUnitBtn?.addEventListener("click", () => this.openModal());

    // Modal close button
    this.dom.modalClose?.addEventListener("click", () => this.closeModal());

    // Modal backdrop click
    this.dom.modal?.addEventListener("click", (e) => {
      if (e.target === this.dom.modal) this.closeModal();
    });

    // ESC key to close modal
    this.dom.modal?.addEventListener("cancel", (e) => {
      e.preventDefault();
      this.closeModal();
    });

    // Add to cart
    this.dom.addToCartBtn?.addEventListener("click", () => this.addToCart());
  }

  /* ------------------------------------------------
   * Step 1 — Main Unit Selection
   * ---------------------------------------------- */
  selectMain(capacity) {
    this.selectedMainCapacity = capacity;
    this.selectedMainVariant = this.config.mainProduct.variants.find(
      (v) => v.capacity === capacity,
    );
    this.currentRule = this.config.rules.find(
      (r) => r.mainCapacity === capacity,
    );

    if (!this.currentRule || !this.selectedMainVariant) return;

    // Update button states
    this.dom?.mainOptions
      ?.querySelectorAll(".grouped-capacity-btn")
      .forEach((btn) => {
        btn.classList.toggle(
          "is-selected",
          parseInt(btn.dataset.capacity, 10) === capacity,
        );
      });

    // Update main unit location stock
    if (this.dom?.mainInventory) {
      this.dom.mainInventory.innerHTML = this.renderLocationStockHtml(
        this.selectedMainVariant?.locationStock,
      );
    }

    // Reset unit slots (start empty)
    this.unitSlots = [];
    this.renderUnitsStep();
    if (this.dom) {
      this.show(this.dom.unitsStep);
    }
    this.updateSummary();
  }

  /* ------------------------------------------------
   * Step 2 — Unit Slots
   * ---------------------------------------------- */
  removeUnitSlot(index) {
    if (!this.unitSlots) return;
    this.unitSlots.splice(index, 1);
    this.renderUnitsStep();
    this.updateSummary();
  }

  /**
   * Given the current unit selections, determine which capacities
   * are valid for a new slot or the slot at `slotIndex`.
   */
  getAvailableCapacities(slotIndex) {
    if (!this.currentRule || !this.unitSlots) return new Set();
    const totalSlots = this.unitSlots.length;
    const otherValues = this.unitSlots
      .filter((v, i) => i !== slotIndex && v !== null)
      .map((s) => s.capacity);

    const available = new Set();

    for (const combo of this.currentRule.combos) {
      if (combo.length !== totalSlots) continue;

      const remaining = [...combo];
      let compatible = true;

      for (const val of otherValues) {
        const idx = remaining.indexOf(val);
        if (idx === -1) {
          compatible = false;
          break;
        }
        remaining.splice(idx, 1);
      }

      if (compatible) {
        remaining.forEach((v) => available.add(v));
      }
    }
    return available;
  }

  /**
   * Get capacities that could be valid if we add one more unit.
   * Checks combos of any valid length (from newTotal up to maxUnits),
   * because we don't know how many total units the user will add.
   */
  getAvailableCapacitiesForNewSlot() {
    if (!this.currentRule || !this.unitSlots) return new Set();
    const currentValues = this.unitSlots.map((s) => s.capacity);

    const available = new Set();

    for (const combo of this.currentRule.combos) {
      // Combo must be at least long enough to contain current + 1 new
      if (combo.length < currentValues.length + 1) continue;

      const remaining = [...combo];
      let compatible = true;

      for (const val of currentValues) {
        const idx = remaining.indexOf(val);
        if (idx === -1) {
          compatible = false;
          break;
        }
        remaining.splice(idx, 1);
      }

      if (compatible) {
        remaining.forEach((v) => available.add(v));
      }
    }

    return available;
  }

  /**
   * Check whether the current full selection is a valid combo.
   */
  isComboValid() {
    if (!this.currentRule || !this.unitSlots) return false;
    if (this.unitSlots.length === 0) return false;

    const sorted = this.unitSlots.map((s) => s.capacity).sort((a, b) => a - b);

    return this.currentRule.combos.some((combo) => {
      if (combo.length !== sorted.length) return false;
      const cs = [...combo].sort((a, b) => a - b);
      return cs.every((v, i) => v === sorted[i]);
    });
  }

  /* ------------------------------------------------
   * Modal — Unit Product Picker
   * ---------------------------------------------- */
  openModal() {
    const rule = this.currentRule;
    if (!rule || !this.dom) return;
    if (this.unitSlots.length >= rule.maxUnits) return;

    this.modalSelectedProduct = null;
    this.modalSelectedVariant = null;

    this.renderModalProducts();
    this.hide(this.dom.modalDetail);

    this.dom.modal?.showModal();
    document.body.style.overflow = "hidden";
  }

  closeModal() {
    this.dom?.modal?.close();
    document.body.style.overflow = "";
  }

  renderModalProducts() {
    const container = this.dom?.modalProducts;
    if (!container) return;

    const products = this.config.unitProducts;
    const availableCapacities = this.getAvailableCapacitiesForNewSlot();

    container.innerHTML = products
      .map((p) => {
        const hasAvailableVariant = p.variants.some(
          (v) => v.available && availableCapacities.has(v.capacity),
        );
        return `
          <button
            type="button"
            class="grouped-modal__product-card${!hasAvailableVariant ? " is-disabled" : ""}"
            data-product-id="${p.id}"
            ${!hasAvailableVariant ? "disabled" : ""}
          >
            <div class="grouped-modal__product-image">
              ${p.featuredImage ? `<img src="${p.featuredImage}" alt="${this.escapeHtml(p.title)}" loading="lazy">` : '<div class="grouped-modal__product-placeholder"></div>'}
            </div>
            <span class="grouped-modal__product-name">${this.escapeHtml(p.title)}</span>
          </button>
        `;
      })
      .join("");

    // Bind click events
    container.onclick = (e) => {
      const card = e.target.closest(".grouped-modal__product-card");
      if (!card || card.disabled) return;
      this.selectModalProduct(card.dataset.productId);
    };
  }

  selectModalProduct(productId) {
    const product = this.config.unitProducts.find(
      (p) => String(p.id) === String(productId),
    );
    if (!product) return;

    this.modalSelectedProduct = product;
    this.modalSelectedVariant = null;

    // Highlight selected card
    this.dom?.modalProducts
      ?.querySelectorAll(".grouped-modal__product-card")
      .forEach((card) => {
        card.classList.toggle(
          "is-selected",
          String(card.dataset.productId) === String(productId),
        );
      });

    const detail = this.dom?.modalDetail;
    if (!detail) return;

    // Lock current height to avoid jumps
    const currentH = detail.offsetHeight;
    if (currentH > 0) {
      detail.style.minHeight = currentH + "px";
    }

    // Fade out, update, fade in
    detail.style.opacity = "0";
    setTimeout(() => {
      this.renderModalDetail(product);
      this.show(detail);
      requestAnimationFrame(() => {
        detail.style.opacity = "1";
        // Release locked height after transition
        setTimeout(() => {
          detail.style.minHeight = "";
        }, 180);
      });
    }, 150);
  }

  renderModalDetail(product) {
    const detail = this.dom?.modalDetail;
    if (!detail) return;

    const availableCapacities = this.getAvailableCapacitiesForNewSlot();

    detail.innerHTML = `
      <div class="grouped-modal__detail-image">
        ${product.featuredImage ? `<img src="${product.featuredImage}" alt="${this.escapeHtml(product.title)}" loading="lazy">` : ""}
      </div>
      <div class="grouped-modal__detail-info">
        <div class="grouped-modal__detail-text">
          <h4 class="grouped-modal__detail-title">${this.escapeHtml(product.title)}</h4>
          <div class="grouped-modal__detail-price" data-role="modal-variant-price"></div>
          <div class="grouped-modal__detail-inventory" data-role="modal-variant-inventory"></div>
        </div>
        <div class="grouped-modal__variants">
          ${product.variants
            .map((v) => {
              const isAvail =
                v.available && availableCapacities.has(v.capacity);
              return `<button type="button"
                class="grouped-unit-btn"
                data-variant-id="${v.id}"
                data-capacity="${v.capacity}"
                ${!isAvail ? "disabled" : ""}
              >${v.capacity} BTU</button>`;
            })
            .join("")}
        </div>
        <button
          type="button"
          class="grouped-modal__confirm-btn"
          data-role="modal-confirm"
          disabled
        >${this.dataset.confirmLabel || "Add"}</button>
      </div>
    `;
    const isGroupedPriceHidden = !this.dom?.totalPrice;
    // Variant selection
    detail.querySelectorAll(".grouped-unit-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        if (btn.disabled) return;
        detail
          .querySelectorAll(".grouped-unit-btn")
          .forEach((b) => b.classList.remove("is-selected"));
        btn.classList.add("is-selected");

        const variantId = parseInt(btn.dataset.capacity, 10);
        this.modalSelectedVariant = product.variants.find(
          (v) => v.capacity === variantId,
        );

        // Update price
        if (!isGroupedPriceHidden) {
          const priceEl = detail.querySelector(
            '[data-role="modal-variant-price"]',
          );
          if (priceEl && this.modalSelectedVariant) {
            priceEl.textContent = this.modalSelectedVariant.priceFormatted;
          }
        }

        // Update inventory via locationStock
        const invEl = detail.querySelector('[data-role="modal-variant-inventory"]');

        if (invEl && this.modalSelectedVariant) {
          const html = this.renderLocationStockHtml(
            this.modalSelectedVariant.locationStock,
          );
          invEl.innerHTML = html;
          invEl.style.display = html ? '' : 'none';
        } else if (invEl) {
          invEl.style.display = 'none';
        }

        // Enable confirm
        const confirmBtn = detail.querySelector('[data-role="modal-confirm"]');
        if (confirmBtn) confirmBtn.disabled = false;
      });
    });

    // Confirm button
    const confirmBtn = detail.querySelector('[data-role="modal-confirm"]');
    confirmBtn?.addEventListener("click", () => {
      if (!this.modalSelectedProduct || !this.modalSelectedVariant) return;
      this.confirmModalSelection();
    });

    this.show(detail);
  }

  confirmModalSelection() {
    const product = this.modalSelectedProduct;
    const variant = this.modalSelectedVariant;
    if (!product || !variant) return;

    this.unitSlots.push({
      productId: product.id,
      variantId: variant.id,
      capacity: variant.capacity,
    });

    this.closeModal();
    this.renderUnitsStep(true, this.unitSlots.length - 1);
    this.updateSummary();
  }

  /* ------------------------------------------------
   * Rendering
   * ---------------------------------------------- */
  /**
   * @param {boolean} animate - whether to animate slot appearance
   * @param {number} animateIndex - index of the slot to animate (-1 = all)
   */
  renderUnitsStep(animate = false, animateIndex = -1) {
    const rule = this.currentRule;
    if (!rule || !this.dom) return;
    const container = this.dom.unitSlotsContainer;
    if (!container) return;

    const isGroupedPriceHidden = !this.dom?.totalPrice;

    container.innerHTML = "";

    this.unitSlots.forEach((slot, index) => {
      const product = this.findUnitProduct(slot.productId);
      const variant = product?.variants.find(
        (v) => v.capacity === slot.capacity,
      );
      if (!product || !variant) return;

      const el = document.createElement("div");
      const shouldAnimate =
        animate && (animateIndex === -1 || animateIndex === index);
      el.className =
        "grouped-unit-slot" +
        (shouldAnimate ? " grouped-unit-slot--animate" : "");
      el.innerHTML = `
        <div class="grouped-unit-slot__product-info">
          ${product.featuredImage ? `<img class="grouped-unit-slot__thumb" src="${product.featuredImage}" alt="${this.escapeHtml(product.title)}" loading="lazy">` : ""}
          <div class="grouped-unit-slot__text">
            <span class="grouped-unit-slot__label">${this.escapeHtml(product.title)}</span>
            <span class="grouped-unit-slot__capacity">${slot.capacity} BTU</span>
          </div>
        </div>
        <span class="grouped-unit-slot__price">${isGroupedPriceHidden ? "" : variant.priceFormatted}</span>
        <button type="button" class="grouped-unit-slot__remove" data-remove="${index}" title="Remove">&times;</button>
      `;
      container.appendChild(el);
    });

    // Bind remove
    container.onclick = (e) => {
      const removeBtn = e.target.closest(".grouped-unit-slot__remove");
      if (removeBtn) {
        this.removeUnitSlot(parseInt(removeBtn.dataset.remove, 10));
      }
    };

    // Update counter
    const template = this.dataset.unitsLimitLabel || "Units: [count]";
    if (this.dom.unitCounter) {
      this.dom.unitCounter.textContent = template.replace(
        "[count]",
        rule.maxUnits.toString(),
      );
    }

    // Show / hide "Add unit" button
    if (this.unitSlots.length < rule.maxUnits) {
      this.show(this.dom?.addUnitBtn);
    } else {
      this.hide(this.dom?.addUnitBtn);
    }
  }

  updateSummary() {
    const rule = this.currentRule;
    if (!this.dom || !this.selectedMainVariant || !rule) {
      if (this.dom) {
        this.hide(this.dom.summary);
        if (this.dom.addToCartBtn) this.dom.addToCartBtn.disabled = true;
      }
      return;
    }

    this.show(this.dom.summary);

    const isGroupedPriceHidden = !this.dom?.totalPrice;

    // Build summary items
    let totalPrice = this.selectedMainVariant.price;
    let html = `
      <div class="grouped-summary__item">
        <span>
          <span class="grouped-summary__item-name">${this.escapeHtml(this.config.mainProduct.title)}</span>
          <span class="grouped-summary__item-capacity">${this.selectedMainCapacity} BTU</span>
        </span>
        <span class="grouped-summary__item-price">${isGroupedPriceHidden ? "" : this.selectedMainVariant.priceFormatted}</span>
      </div>
    `;

    this.unitSlots.forEach((slot) => {
      const product = this.findUnitProduct(slot.productId);
      const v = product?.variants.find((u) => u.capacity === slot.capacity);
      if (v) {
        totalPrice += v.price;
        html += `
          <div class="grouped-summary__item">
            <div class="grouped-summary__name-info">
              <span class="grouped-summary__item-name">${this.escapeHtml(product.title)}</span>
              <span class="grouped-summary__item-capacity">${slot.capacity} BTU</span>
            </div>
            <span class="grouped-summary__item-price">${isGroupedPriceHidden ? "" : v.priceFormatted}</span>
          </div>
        `;
      }
    });

    if (this.dom?.summaryItems) {
      this.dom.summaryItems.innerHTML = html;
    }
    if (this.dom?.totalPrice) {
      this.dom.totalPrice.textContent = this.formatMoney(totalPrice);
    }

    // Validate combo
    const valid = this.isComboValid();

    const msgSelectAll = this.dataset.msgSelectAll || "";
    const msgInvalid = this.dataset.msgInvalid || "";

    const statusEl = this.dom?.comboStatus;
    const cartBtn = this.dom?.addToCartBtn;

    if (this.unitSlots.length === 0) {
      if (statusEl) {
        statusEl.textContent = msgSelectAll;
        statusEl.className = "grouped-summary__status";
      }
      if (cartBtn) cartBtn.disabled = true;
    } else if (valid) {
      if (statusEl) {
        statusEl.textContent = "";
        statusEl.className = "grouped-summary__status--valid";
      }
      if (cartBtn) cartBtn.disabled = false;
    } else {
      if (statusEl) {
        statusEl.textContent =
          this.unitSlots.length < 2 ? msgSelectAll : msgInvalid;
        statusEl.className =
          this.unitSlots.length < 2
            ? "grouped-summary__status"
            : "grouped-summary__status grouped-summary__status--invalid";
      }
      if (cartBtn) cartBtn.disabled = true;
    }
  }

  /* ------------------------------------------------
   * Cart
   * ---------------------------------------------- */
  async addToCart() {
    const rule = this.currentRule;
    if (!rule || !this.isComboValid() || this.isAddingToCart) return;

    const mainVariant = this.selectedMainVariant;
    if (!mainVariant) return;

    this.isAddingToCart = true;

    const bundleId = crypto.randomUUID();
    const parentBundleId = this.config.bundle_parent || {};

    const sharedProps = {
      _bundle_id: bundleId,
      _bundle_parent: parentBundleId,
    };

    const items = [];

    // Main unit
    items.push({
      id: mainVariant.id,
      quantity: 1,
      properties: { ...sharedProps },
    });

    // Indoor units — group identical variant IDs
    const unitCounts = {};
    const slots = this.unitSlots || [];
    slots.forEach((slot) => {
      unitCounts[slot.variantId] = (unitCounts[slot.variantId] || 0) + 1;
    });
    Object.entries(unitCounts).forEach(([id, qty]) => {
      items.push({
        id: parseInt(id, 10),
        quantity: qty,
        properties: { ...sharedProps },
      });
    });

    const cartBtn = this.dom?.addToCartBtn;
    if (cartBtn) {
      cartBtn.disabled = true;
      cartBtn.classList.add("is-loading");
    }

    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      // Success: trigger checkmark-burst animation
      if (cartBtn) {
        cartBtn.classList.remove("is-loading");
        cartBtn.dataset.added = "true";

        setTimeout(() => {
          if (cartBtn) {
            cartBtn.removeAttribute("data-added");
            cartBtn.disabled = false;
          }
          this.isAddingToCart = false;
        }, 2000);
      } else {
        this.isAddingToCart = false;
      }

      // Update cart count
      try {
        const cartRes = await fetch("/cart.js");
        const cart = await cartRes.json();

        document.dispatchEvent(
          new CustomEvent("cart:update", {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: "grouped-configurator",
              data: {
                itemCount: cart.item_count,
                source: "grouped-configurator",
              },
            },
          }),
        );
      } catch (_) {
        window.location.reload();
      }
    } catch (err) {
      console.error("Grouped add to cart failed:", err);
      if (cartBtn) {
        cartBtn.classList.remove("is-loading");
        cartBtn.disabled = false;

        const textEl = cartBtn.querySelector('[data-role="add-to-cart-text"]');
        if (textEl) {
          const origHTML = textEl.innerHTML;
          textEl.textContent = this.dataset.msgCartError || "Error, try again";
          setTimeout(() => {
            textEl.innerHTML = origHTML;
          }, 2500);
        }
      }
      this.isAddingToCart = false;
    }
  }

  /* ------------------------------------------------
   * Utilities
   * ---------------------------------------------- */
  findUnitProduct(productId) {
    return this.config.unitProducts.find(
      (p) => String(p.id) === String(productId),
    );
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const format = this.config.moneyFormat || "${{amount}}";
    return format
      .replace("{{amount}}", amount)
      .replace("{{amount_no_decimals}}", Math.round(cents / 100))
      .replace("{{amount_with_comma_separator}}", amount.replace(".", ","));
  }

  escapeHtml(str) {
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }

  show(el) {
    if (el) el.classList.remove("grouped-step--hidden");
  }

  hide(el) {
    if (el) el.classList.add("grouped-step--hidden");
  }
}

if (!customElements.get("grouped-configurator")) {
  customElements.define("grouped-configurator", GroupedConfigurator);
}
